import { useRef, useState } from "react";
import axios from "axios";
import { API, errText } from "@/lib/api";
import { track, getAttribution } from "@/lib/tracking";
import { CtaButton, Kicker } from "@/components/landing/ui";
import { FurnitureStep, ColourStep, ConcernsStep, ContactStep } from "@/components/landing/FormSteps";
import { PhotoStep } from "@/components/landing/PhotoStep";
import { SuccessState } from "@/components/landing/SuccessState";
import { ArrowLeft } from "lucide-react";

const STEPS = 5;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const initial = {
  furniture_types: [], furniture_other: "", colour_goal: "", concerns: [], concerns_other: "", timeline: "",
  first_name: "", last_name: "", email: "", phone: "", postal_code: "", notes: "",
};

const validate = (step, f, photos) => {
  if (step === 0 && f.furniture_types.length === 0) return "Choose at least one type of furniture to continue.";
  if (step === 1 && !f.colour_goal) return "Let us know what you'd like to do with the colour.";
  if (step === 2 && !f.timeline) return "Choose a rough timeline to continue.";
  if (step === 3) {
    if (photos.some((p) => p.uploading)) return "Hang on — your photos are still uploading.";
    if (!photos.some((p) => p.id)) return "Please add at least one photo of your furniture.";
  }
  if (step === 4) {
    if (!f.first_name.trim() || !f.last_name.trim()) return "Please enter your first and last name.";
    if (!EMAIL_RE.test(f.email.trim())) return "Please enter a valid email address.";
    if (f.phone.replace(/\D/g, "").length < 10) return "Please enter a valid phone number.";
    if (f.postal_code.trim().length < 3) return "Please enter your postal code.";
  }
  return null;
};

export const QualifyForm = ({ gallery }) => {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(initial);
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const started = useRef(false);
  const topRef = useRef();

  const set = (patch) => {
    if (!started.current) {
      started.current = true;
      track("QualificationStarted");
    }
    setError(null);
    setF((prev) => ({ ...prev, ...patch }));
  };

  const go = (next) => {
    setError(null);
    setStep(next);
    setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const next = () => {
    const err = validate(step, f, photos);
    if (err) return setError(err);
    go(step + 1);
  };

  const submit = async () => {
    const err = validate(4, f, photos);
    if (err) return setError(err);
    setSubmitting(true);
    const attr = getAttribution();
    try {
      const { data } = await axios.post(`${API}/public/leads`, {
        ...f,
        email: f.email.trim(),
        photo_ids: photos.filter((p) => p.id).map((p) => p.id),
        utm: attr.utm || {},
        landing_url: attr.landing_url || window.location.href,
        referrer: attr.referrer || "",
      });
      track("LeadSubmitted", { lead_number: data.lead_number, qualification: data.qualification, furniture: f.furniture_types.join(", ") });
      setResult(data);
      setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      setError(errText(e));
    } finally {
      setSubmitting(false);
    }
  };

  const pct = Math.round(((step + 1) / STEPS) * 100);

  return (
    <section id="qualify" className="bg-white py-20 sm:py-28 scroll-mt-4" data-testid="qualify-form-section">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={topRef} className="max-w-2xl mx-auto text-center scroll-mt-6">
          <Kicker>Quick qualification</Kicker>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">See if your furniture qualifies.</h2>
          <p className="text-gray-600 text-lg leading-relaxed mt-6">
            Answer a few quick questions and we'll determine whether Furniture Revival looks like the right solution.
          </p>
        </div>

        <div className="max-w-2xl mx-auto mt-12 border border-gray-200 shadow-[0_24px_70px_rgba(0,0,0,0.07)] bg-white">
          {result ? (
            <SuccessState result={result} gallery={gallery} firstName={f.first_name} />
          ) : (
            <div className="p-6 sm:p-10">
              <div className="mb-8" data-testid="form-progress">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2.5">
                  <span data-testid="form-step-label">Step {step + 1} of {STEPS}</span>
                  <span>{pct}%</span>
                </div>
                <div className="h-1 bg-gray-200">
                  <div className="h-full bg-[#c19a6b] transition-[width] duration-300" style={{ width: `${pct}%` }} />
                </div>
              </div>

              {step === 0 && <FurnitureStep f={f} set={set} />}
              {step === 1 && <ColourStep f={f} set={set} />}
              {step === 2 && <ConcernsStep f={f} set={set} />}
              {step === 3 && <PhotoStep photos={photos} setPhotos={(fn) => { if (!started.current) { started.current = true; track("QualificationStarted"); } setError(null); setPhotos(fn); }} />}
              {step === 4 && <ContactStep f={f} set={set} />}

              {error && <p className="mt-6 text-sm text-red-600 bg-red-50 px-4 py-3" data-testid="form-error" role="alert">{error}</p>}

              <div className="mt-9 flex flex-col-reverse sm:flex-row items-center justify-between gap-4">
                {step > 0 ? (
                  <button type="button" data-testid="form-back-btn" onClick={() => go(step - 1)} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black transition-colors duration-150 h-12">
                    <ArrowLeft size={15} /> Back
                  </button>
                ) : <span />}
                {step < STEPS - 1 ? (
                  <button type="button" data-testid="form-next-btn" onClick={next} className="w-full sm:w-auto h-14 px-10 bg-black text-white text-[12px] uppercase tracking-[0.22em] font-bold btn-lift">
                    Continue
                  </button>
                ) : (
                  <CtaButton testId="form-submit-btn" onClick={submit} disabled={submitting} className="w-full sm:w-auto">
                    {submitting ? "Sending…" : "See if my furniture qualifies"}
                  </CtaButton>
                )}
              </div>
              {step === STEPS - 1 && (
                <p className="text-xs text-gray-500 leading-relaxed mt-6 text-center sm:text-left">
                  By submitting this form, you're asking ARK Furniture to contact you about this furniture project. There is no obligation to proceed.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
